import api, { ensureAuthContractKnown, buildLastLogoutReasonHeaders } from "./api";
import { applyWebSessionTokens } from "../utils/sessionRevocation";
// WEB DEVICE LOGIN APPROVAL (disabled) — uncomment the import + payload block below to require
// institute admin approval before web users can sign in (mobile uses its own device flow).
// import { getWebDeviceId, getWebDeviceName } from "../utils/webDeviceId";

// Central role normalizer/mapping for frontend
// Backend: "institute" should be treated exactly like "admin" on the frontend
const normalizeRole = (rawRole) => {
  if (!rawRole) return null;
  const role = String(rawRole).toLowerCase().trim();

  if (role === "institute") return "admin";
  if (role === "super admin" || role === "superadmin") return "superadmin";

  return role.replace(/\s+/g, "");
};

// Demo credentials for testing
// Email: student@demo.com, teacher@demo.com, hod@demo.com, admin@demo.com, superadmin@demo.com
// Password: demo123 (for all accounts)

const demoUsers = {
  "student@demo.com": {
    id: 1,
    name: "John Student",
    email: "student@demo.com",
    role: "student",
    department: "Computer Science",
    consentAccepted: true,
  },
  "teacher@demo.com": {
    id: 2,
    name: "Dr. Sarah Teacher",
    email: "teacher@demo.com",
    role: "teacher",
    department: "Computer Science",
    consentAccepted: true,
  },
  "hod@demo.com": {
    id: 3,
    name: "Prof. Robert HOD",
    email: "hod@demo.com",
    role: "hod",
    department: "Computer Science",
    consentAccepted: true,
  },
  "admin@demo.com": {
    id: 4,
    name: "Admin User",
    email: "admin@demo.com",
    role: "admin",
    department: "Administration",
    consentAccepted: true,
  },
  "superadmin@demo.com": {
    id: 5,
    name: "Super Admin",
    email: "superadmin@demo.com",
    role: "superadmin",
    department: "Platform",
    consentAccepted: false,
  },
};


const authService = {
  login: (credentials) => {
    if (process.env.NODE_ENV === 'development') {
     // console.log('Login attempt with credentials:', { email: credentials.email });
    }
    
    // Try /web/auth/login first (matches refresh token pattern), fallback to /auth/login
    const loginEndpoint = "/auth/login";
    const payload = { ...credentials };

    /* WEB DEVICE LOGIN APPROVAL (disabled) — uncomment to send browser device id on login:
    const deviceId = getWebDeviceId();
    const payload = {
      ...credentials,
      ...(deviceId
        ? {
            deviceId,
            platform: "web",
            deviceName: getWebDeviceName(),
          }
        : {}),
    };
    Also re-enable the matching block in ERPInstituteBackend/controllers/v1/authController.js
    and set skipDeviceAuthorization: false on issueLoginTokens().
    */

    // Learn X-Auth-Contract support first (never throws), so the optional
    // X-Last-Logout-Reason header is only sent to a backend whose CORS allows it.
    return Promise.resolve()
      .then(() => ensureAuthContractKnown())
      .catch((e) => {
        console.warn("[authService.login] contract probe failed:", e?.message || e);
        return false;
      })
      .then(() => api.post(loginEndpoint, payload, { headers: buildLastLogoutReasonHeaders() }))
      .then(async (response) => {
      // Log full response for debugging
      if (process.env.NODE_ENV === 'development') {
      //  console.log('Full login response:', response);
     //   console.log('Response data:', response.data);
      }
      
      // Handle different response structures
      let responseData = response.data;
      if (responseData?.data) {
        responseData = responseData.data;
      }
      
      // Extract token from various possible locations
      const authToken = responseData?.accessToken || responseData?.token || response.data?.accessToken || response.data?.token;
      
      // Extract user from various possible locations
      let userObj = responseData?.user || 
                   responseData?.data?.user || 
                   response.data?.user ||
                   responseData?.admin ||
                   responseData?.data?.admin ||
                   response.data?.admin ||
                   responseData?.profile ||
                   responseData?.data?.profile ||
                   response.data?.profile;
      
      // If no user object but we have a token, try to decode JWT to get user info
      if (authToken && !userObj) {
        try {
          // Decode JWT token (base64 decode the payload)
          const tokenParts = authToken.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (process.env.NODE_ENV === 'development') {
             // console.log('Decoded JWT payload:', payload);
            }
            // Create user object from JWT payload if it contains user data
            if (payload.id || payload.userId || payload.email) {
              // Normalize role to lowercase for consistency
              const rawRole = payload.role || payload.userRole || payload.roleName;
              const normalizedRole = normalizeRole(rawRole);
              
              userObj = {
                id: payload.id || payload.userId || payload.sub,
                email: payload.email,
                role: normalizedRole,
                name: payload.name || payload.username || payload.fullName,
                firstName: payload.firstName || payload.first_name,
                lastName: payload.lastName || payload.last_name,
                consentFlag: payload.consentFlag || payload.consent_flag || 1,
                ...payload // Include any other fields from the token
              };
              
              // Override role with normalized version
              userObj.role = normalizedRole;
              
              if (process.env.NODE_ENV === 'development') {
              //  console.log('Raw role from JWT:', rawRole);
              //  console.log('Normalized role:', normalizedRole);
              //  console.log('Created user object from JWT:', userObj);
              }
            }
          }
        } catch (error) {
          console.warn('Failed to decode JWT:', error);
        }
      }
      
      // Normalize mapped role for any user object we have from backend
      if (userObj) {
        const rawRoleFromUser =
          userObj.role?.slug || userObj.role || userObj.userRole || userObj.roleName;
        userObj.role = normalizeRole(rawRoleFromUser);
      }

      // Restrict student access from web application
      if (userObj?.role === "student") {
        authService.clearLocalSession();
        throw new Error(
          "Student access is restricted to the MyAMS Mobile App. Please download the MyAMS app on Android or iOS to sign in."
        );
      }

      if (process.env.NODE_ENV === 'development') {
        //console.log('Final extracted user (after normalization):', userObj);
       // console.log('Extracted token:', authToken);
      }
      
      const refresh = responseData?.refreshToken || response.data?.refreshToken;
      
      if (authToken) {
        // Writes tokens, records clock skew, clears lastLogoutCode, dispatches myams:session-changed.
        applyWebSessionTokens({ accessToken: authToken, refreshToken: refresh || null });
        if (userObj) {
          localStorage.setItem("user", JSON.stringify(userObj));
        }
      }
      
      // Extract message from backend response
      const backendMessage = responseData?.message || 
                            response.data?.message || 
                            responseData?.data?.message ||
                            null;

      // Return response in the format expected by authSlice
      const normalizedResponse = {
        ...response,
        data: {
          user: userObj,
          accessToken: authToken,
          refreshToken: refresh,
          message: backendMessage,
          consentRequired: responseData?.consentRequired !== undefined
            ? responseData.consentRequired
            : (userObj?.consentFlag === 0),
          activeConsentForm: responseData?.activeConsentForm || null,
          isFirstLogin: responseData?.isFirstLogin || false,
        }
      };
      
      if (process.env.NODE_ENV === 'development') {
      //  console.log('Normalized response:', normalizedResponse.data);
      }
      
      return normalizedResponse;
    })
    .catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Login API Error:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url
        });
      }
      // Re-throw the error so it can be handled by the Redux thunk
      throw error;
    });
  },

  clearLocalSession: () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("selectedInstitute");
    localStorage.removeItem("isInInstituteContext");
  },

  logout: () => {
    const token = localStorage.getItem("accessToken");
    authService.clearLocalSession();
    api.post("/auth/logout", {}, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }).catch(() => null);
    return Promise.resolve({ data: { message: "Logged out" } });
  },

  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),

  verifyOtp: (email, otp) => 
    api.post("/auth/verify-otp", { email, otp }),

  resetPassword: (resetData) => {
    // Extract email, newPassword, confirmPassword, and optionally token
    const { email, newPassword, confirmPassword, token, password } = resetData;
    
    // Build request body with email, newPassword, and confirmPassword
    const requestBody = {
      email: email || "",
      newPassword: newPassword || password, // Support both newPassword and password for backward compatibility
      confirmPassword: confirmPassword || "",
      ...(token && { token }) // Include token if provided
    };
    
    return api.post("/auth/reset-password", requestBody);
  },

  checkAuth: () => {
    // Check if token exists in localStorage
    const token = localStorage.getItem("accessToken");
    const storedUser = localStorage.getItem("user");
    
    if (!token) {
      return Promise.reject({ response: { data: { message: "No token found" } } });
    }
    
    // If we have a stored user, return it
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const rawRole = user.role?.slug || user.role || user.userRole || user.roleName;
        if (rawRole) user.role = normalizeRole(rawRole);
        if (user.role === "student") {
          authService.clearLocalSession();
          localStorage.setItem(
            "logoutReason",
            "Student access is restricted to the MyAMS Mobile App. Please use the mobile app on Android or iOS."
          );
          return Promise.reject({ response: { data: { message: "Student access is restricted to the MyAMS Mobile App." } } });
        }
        return Promise.resolve({
          data: {
            user: user
          }
        });
      } catch (error) {
        console.warn('Failed to parse stored user:', error);
      }
    }
    
    // Try to decode JWT token to get user info
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        
        // Normalize/mapping role for consistency
        const rawRole = payload.role || payload.userRole || payload.roleName;
        const normalizedRole = normalizeRole(rawRole);
        
        if (normalizedRole === "student") {
          authService.clearLocalSession();
          localStorage.setItem(
            "logoutReason",
            "Student access is restricted to the MyAMS Mobile App. Please use the mobile app on Android or iOS."
          );
          return Promise.reject({ response: { data: { message: "Student access is restricted to the MyAMS Mobile App." } } });
        }

        const user = {
          id: payload.id || payload.userId || payload.sub,
          email: payload.email,
          role: normalizedRole,
          name: payload.name || payload.username || payload.fullName,
          firstName: payload.firstName || payload.first_name,
          lastName: payload.lastName || payload.last_name,
          consentFlag: payload.consentFlag || payload.consent_flag || 1,
          ...payload
        };
        
        // Override role with normalized version
        user.role = normalizedRole;
        
        // Store the decoded user
        localStorage.setItem("user", JSON.stringify(user));
        
        if (process.env.NODE_ENV === 'development') {
        //  console.log('checkAuth - Decoded user role:', normalizedRole);
        }
        
        return Promise.resolve({
          data: {
            user: user
          }
        });
      }
    } catch (error) {
      console.warn('Failed to decode token:', error);
    }
    
    // If all else fails, reject
    return Promise.reject({ response: { data: { message: "Unable to verify authentication" } } });
  },

  acceptConsent: (consentFormId) => {
    return api.post("/auth/accept-consent", { 
      consentFlag: true,
      acceptedPrivacyPolicyId: consentFormId 
    });
  },

  refreshToken: (refreshToken) => api.post("/auth/refresh", { refreshToken }),

  changePassword: (oldPassword, newPassword) =>
    api.post("/auth/change-password", { oldPassword, newPassword }),
};

export default authService;
